// src/components/common/Sidebar.tsx
import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  LayoutDashboard,
  Package,
  GitCompare,
  LineChart,
  Clock,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  X,
  Shield,
} from "lucide-react";
import { useSidebar } from "../../contexts/SidebarContext";
import { useModuleAccess } from "../../hooks/useModuleAccess";

export const SIDEBAR_WIDTH_EXPANDED = 240;
export const SIDEBAR_WIDTH_COLLAPSED = 80;
const MOBILE_DRAWER_WIDTH = 280;

const WL_RED = "#EF2D3E";
const WL_RED_BG = "#FEF2F2";
const WL_GRAY = "#374151";
const BORDER_COLOR = "#EAEAEA";
const HOVER_BG = "#F7F7F7";

const mainNavItems = [
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { name: "Orders", path: "/orders", icon: Package },
];

const planningNavItems = [
  { name: "Tariff Calculator", path: "/tariffs", icon: LineChart },
  { name: "Route Optimization", path: "/route-optimization", icon: GitCompare },
];

const baseBottomNavItems = [
  { name: "Reminders", path: "/reminders", icon: Clock },
  { name: "Settings", path: "/settings", icon: Settings },
  { name: "Help Center", path: "/help", icon: HelpCircle },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { isExpanded, setIsExpanded, mobileOpen, setMobileOpen } = useSidebar();
  const { data: moduleAccess } = useModuleAccess();

  const bottomNavItems = [
    ...(moduleAccess?.admin_panel ? [{ name: "Admin", path: "/admin", icon: Shield }] : []),
    ...baseBottomNavItems,
  ];

  const sidebarWidth = isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  const handleNavClick = (path: string, openInNewTab?: boolean) => {
    if (openInNewTab) {
      window.open(path, "_blank");
    } else {
      navigate(path);
    }
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Desktop nav item renderer (supports collapsed/expanded modes)
  const renderDesktopNavItem = (item: typeof mainNavItems[0] & { openInNewTab?: boolean }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    const listItemContent = (
      <ListItemButton
        onClick={() => handleNavClick(item.path, item.openInNewTab)}
        sx={{
          borderRadius: "12px",
          minHeight: 44,
          py: 1.5,
          px: isExpanded ? 2 : 0,
          justifyContent: isExpanded ? "flex-start" : "center",
          position: "relative",
          color: isActive ? WL_RED : WL_GRAY,
          bgcolor: isActive ? WL_RED_BG : "transparent",
          "&:hover": {
            bgcolor: isActive ? WL_RED_BG : HOVER_BG,
            color: isActive ? WL_RED : "#000000",
          },
          overflow: "hidden",
        }}
      >
        {/* Active indicator bar */}
        {isActive && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              bgcolor: WL_RED,
              borderRadius: "0 3px 3px 0",
            }}
          />
        )}

        <ListItemIcon
          sx={{
            minWidth: isExpanded ? 36 : 40,
            width: isExpanded ? "auto" : 40,
            height: 40,
            color: "inherit",
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Icon size={20} strokeWidth={1.75} />
        </ListItemIcon>

        {isExpanded && (
          <ListItemText
            primary={item.name}
            primaryTypographyProps={{
              sx: {
                fontSize: "0.875rem",
                fontWeight: 500,
                whiteSpace: "nowrap",
                fontFamily: '"Plus Jakarta Sans", sans-serif',
              },
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.path} disablePadding sx={{ px: 0 }}>
        {isExpanded ? (
          listItemContent
        ) : (
          <Tooltip title={item.name} placement="right" arrow>
            {listItemContent}
          </Tooltip>
        )}
      </ListItem>
    );
  };

  // Mobile nav item renderer (always shows icon + label in full row)
  const renderMobileNavItem = (item: typeof mainNavItems[0] & { openInNewTab?: boolean }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    return (
      <ListItem key={item.path} disablePadding>
        <ListItemButton
          onClick={() => handleNavClick(item.path, item.openInNewTab)}
          sx={{
            borderRadius: "10px",
            minHeight: 48,
            py: 1.5,
            px: 2,
            position: "relative",
            color: isActive ? WL_RED : WL_GRAY,
            bgcolor: isActive ? WL_RED_BG : "transparent",
            "&:hover": {
              bgcolor: isActive ? WL_RED_BG : HOVER_BG,
              color: isActive ? WL_RED : "#000000",
            },
            overflow: "visible",
          }}
        >
          {/* Active indicator bar */}
          {isActive && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                height: 24,
                width: 3,
                bgcolor: WL_RED,
                borderRadius: "0 2px 2px 0",
              }}
            />
          )}

          <ListItemIcon
            sx={{
              minWidth: 40,
              color: "inherit",
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Icon size={22} strokeWidth={1.75} />
          </ListItemIcon>

          <ListItemText
            primary={item.name}
            primaryTypographyProps={{
              sx: {
                fontSize: "0.9375rem",
                fontWeight: isActive ? 600 : 500,
                whiteSpace: "nowrap",
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                ml: 0.5,
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  // Desktop drawer content (supports collapsed/expanded modes)
  const desktopDrawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#FFFFFF",
      }}
    >
      {/* Sidebar Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          display: "flex",
          alignItems: "center",
          justifyContent: isExpanded ? "space-between" : "center",
          minHeight: 56,
        }}
      >
        {isExpanded && (
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "uppercase",
              color: "#9CA3AF",
              letterSpacing: "0.05em",
              fontFamily: '"Plus Jakarta Sans", sans-serif',
            }}
          >
            Supply Chain
          </Typography>
        )}
        <IconButton
          onClick={() => setIsExpanded(!isExpanded)}
          size="small"
          sx={{
            borderRadius: "6px",
            p: 0.75,
            "&:hover": {
              bgcolor: HOVER_BG,
            },
          }}
        >
          {isExpanded ? (
            <ChevronLeft size={16} color="#6B7280" />
          ) : (
            <ChevronRight size={16} color="#6B7280" />
          )}
        </IconButton>
      </Box>

      {/* Main Navigation Items */}
      <Box sx={{ pt: 2, px: isExpanded ? 1.5 : 2, flexGrow: 0 }}>
        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {mainNavItems.map(renderDesktopNavItem)}
        </List>
      </Box>

      {/* Planning Section */}
      <Box sx={{ pt: 1, pb: 0.5, px: isExpanded ? 1.5 : 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5 }}>
          {isExpanded ? (
            <>
              <Box sx={{ height: "1px", flex: 1, bgcolor: "#E5E7EB" }} />
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "#9CA3AF",
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  whiteSpace: "nowrap",
                }}
              >
                Planning
              </Typography>
              <Box sx={{ height: "1px", flex: 1, bgcolor: "#E5E7EB" }} />
            </>
          ) : (
            <Box sx={{ height: "1px", width: "100%", bgcolor: "#E5E7EB" }} />
          )}
        </Box>
      </Box>
      <Box sx={{ px: isExpanded ? 1.5 : 2, flexGrow: 0 }}>
        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {planningNavItems.map(renderDesktopNavItem)}
        </List>
      </Box>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Bottom Navigation Items */}
      <Box sx={{ px: isExpanded ? 1.5 : 2, pb: 2.5 }}>
        <Divider sx={{ mb: 2, borderColor: BORDER_COLOR }} />
        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {bottomNavItems.map(renderDesktopNavItem)}
        </List>
      </Box>
    </Box>
  );

  // Mobile drawer content (always shows full rows with icons + labels)
  const mobileDrawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#FFFFFF",
      }}
    >
      {/* Mobile Drawer Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 60,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 700,
            textTransform: "uppercase",
            color: "#6B7280",
            letterSpacing: "0.08em",
            fontFamily: '"Plus Jakarta Sans", sans-serif',
          }}
        >
          Supply Chain
        </Typography>
        <IconButton
          onClick={() => setMobileOpen(false)}
          size="small"
          aria-label="Close navigation"
          sx={{
            borderRadius: "8px",
            p: 1,
            color: "#6B7280",
            "&:hover": {
              bgcolor: HOVER_BG,
              color: "#374151",
            },
          }}
        >
          <X size={20} strokeWidth={2} />
        </IconButton>
      </Box>

      {/* Main Navigation Items */}
      <Box sx={{ pt: 2, px: 1.5, flexGrow: 0 }}>
        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {mainNavItems.map(renderMobileNavItem)}
        </List>
      </Box>

      {/* Planning Section */}
      <Box sx={{ pt: 1, pb: 0.5, px: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5 }}>
          <Box sx={{ height: "1px", flex: 1, bgcolor: "#E5E7EB" }} />
          <Typography
            sx={{
              fontSize: "0.625rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "#9CA3AF",
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              whiteSpace: "nowrap",
            }}
          >
            Planning
          </Typography>
          <Box sx={{ height: "1px", flex: 1, bgcolor: "#E5E7EB" }} />
        </Box>
      </Box>
      <Box sx={{ px: 1.5, flexGrow: 0 }}>
        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {planningNavItems.map(renderMobileNavItem)}
        </List>
      </Box>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Bottom Navigation Items */}
      <Box sx={{ px: 1.5, pb: 3 }}>
        <Divider sx={{ mb: 2, borderColor: BORDER_COLOR }} />
        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {bottomNavItems.map(renderMobileNavItem)}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Desktop Drawer - Permanent */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          elevation={0}
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: sidebarWidth,
              boxSizing: "border-box",
              top: 56,
              height: "calc(100vh - 56px)",
              borderRight: `1px solid ${BORDER_COLOR}`,
              boxShadow: "none",
              bgcolor: "#FFFFFF",
              transition: "width 300ms ease",
              overflowX: "hidden",
            },
          }}
        >
          {desktopDrawerContent}
        </Drawer>
      )}

      {/* Mobile Drawer - Temporary with full navigation layout */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: MOBILE_DRAWER_WIDTH,
              boxSizing: "border-box",
              top: 56,
              height: "calc(100vh - 56px)",
              borderRight: `1px solid ${BORDER_COLOR}`,
              boxShadow: "none",
              bgcolor: "#FFFFFF",
            },
          }}
        >
          {mobileDrawerContent}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;